import {useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {useFormik} from 'formik'
import {AxiosError} from 'axios'
import {toast} from 'react-toastify'
import {useMutation} from 'react-query'

import {requestPassword} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {forgotPasswordModel} from '../form/models'
import {convertToTitle} from '../../../../_metronic/helpers'

import {Input} from '../../../../_metronic/layout/components/Forms/Input'

export function ForgotPassword() {
  const {checkLogin} = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [hideForm, setHideForm] = useState(false)

  const {mutate} = useMutation(
    'forgotPassword',
    async (email: string) => await requestPassword(email)
  )

  const formik = useFormik({
    initialValues: forgotPasswordModel.initials,
    validationSchema: forgotPasswordModel.schema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      mutate(values.email, {
        onSuccess() {
          setHideForm(true)
          toast.success('Password reset email sent')
          navigate('/auth/login')
        },
        onError(error) {
          const errMsg: string = ((error as AxiosError).response?.data as any)?.message
          setStatus(convertToTitle(errMsg.toLocaleLowerCase()) || 'Something went wrong')
          toast.error(errMsg)
          checkLogin()
        },
        onSettled() {
          setSubmitting(false)
          setLoading(false)
        },
      })
    },
  })

  const hasErrors = !!formik.status || !!formik.errors.email

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-dark mb-3'>Forgot Password?</h1>
        {/* end::Title */}

        {/* begin::Link */}
        <div className='text-gray-400 fw-bold fs-4'>Enter your email to reset your password.</div>
        {/* end::Link */}
      </div>

      {/* begin::Title */}
      {hasErrors && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold text-capitalize'>
            {formik.status || formik.errors.email}
          </div>
        </div>
      )}

      {!hasErrors && hideForm && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>Sent password reset. Please check your email</div>
        </div>
      )}
      {/* end::Title */}

      {/* begin::Form group */}
      {!hideForm && (
        <Input
          type='email'
          containerClass='fv-row mb-10'
          label='Email'
          id='forgot-email'
          {...formik.getFieldProps('email')}
          inputTouched={formik.touched.email}
          inputError={formik.errors.email}
        />
      )}

      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button
          disabled={formik.isSubmitting || hideForm}
          type='submit'
          id='kt_password_reset_submit'
          className='btn btn-lg btn-primary fw-bolder me-4'
        >
          <span className='indicator-label'>Submit</span>
          {loading && (
            <span className='indicator-progress'>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-lg btn-light-primary fw-bolder'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!hasErrors && hideForm ? 'Back to Login' : 'Cancel'}
          </button>
        </Link>{' '}
      </div>
      {/* end::Form group */}
    </form>
  )
}
